import orderBy from "lodash/orderBy";
import { matchSorter } from "match-sorter";
import numeral from "numeral";
import type { ReactElement } from "react";
import { useState } from "react";
import * as React from "react";
import type { Omit } from "utility-types";
import type { IBrazilianRealEstateFundTopLevelFragment } from "../../lib/fragments/__generated__/BrazilianRealEstateFundTopLevel.generated";
import usePagination from "../../lib/hooks/usePagination";
import type { IBrazilianRealEstateFundGalleryPicture } from "../../types/graphqlTypes";
import BuildingIcon from "../icons/BuildingIcon";
import SortAscendingIcon from "../icons/SortAscendingIcon";
import SortDescendingIcon from "../icons/SortDescendingIcon";
import SortIcon from "../icons/SortIcon";
import * as palette from "../palette";
import GalleryCarousel from "./GalleryCarousel";
import GalleryLightbox from "./GalleryLightbox";
import filterUnionByTypename from "../../lib/utils/filterUnionByTypename";

interface IPropertiesToRentProps {
  realEstateFund: IBrazilianRealEstateFundTopLevelFragment;
}

function adjustedItemsPerPage<T>(searchQuery: string, propertiesToRent: T[]): number {
  if (searchQuery.length > 0) {
    return propertiesToRent.length <= 25 ? propertiesToRent.length : 20;
  }

  // if `propertiesToRent.length` is 11 we don't actually need to show two pages. But if it's >= 15 we paginate so we'd
  // have a more "filled" 2nd page
  return propertiesToRent.length <= 15 ? propertiesToRent.length : 10;
}

export default function PropertiesToRent({ realEstateFund }: IPropertiesToRentProps): ReactElement {
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState<"location.city" | "location.state" | "area" | "vacancyRate" | "">("");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc" | "">("");

  const searchResults = matchSorter(
    filterUnionByTypename(realEstateFund.properties, "BrazilianRealEstateFundPropertyToRent"),
    searchQuery,
    {
      keys: ["name", "location.formatted", "location.abbreviatedStateName", "location.city", "location.state"],
      threshold: matchSorter.rankings.CONTAINS,
      keepDiacritics: false,
    },
  );

  const propertiesToRent =
    sortBy === "" && sortDirection === "" ? searchResults : orderBy(searchResults, [sortBy], [sortDirection as any]);

  const pagination = usePagination({
    items: propertiesToRent,
    itemsPerPage: adjustedItemsPerPage(searchQuery, propertiesToRent),
  });

  return (
    <div
      css={{
        position: "relative",
        gridColumn: "span 12",
        backgroundColor: "#ffffff",
        borderRadius: 4,
        boxShadow: "0 4px 6px 0 #33333333",
      }}
    >
      <div
        css={{
          display: "grid",
          gridRowGap: 16,
          padding: "16px 0px",
        }}
      >
        <div
          css={{
            display: "grid",
            gridColumnGap: 16,
            gridTemplateColumns: "auto 1fr auto",
            alignItems: "center",
          }}
        >
          <div
            css={{
              display: "inline-grid",
              gridAutoColumns: "max-content",
              gridAutoFlow: "column",
              gridColumnGap: 12,
              alignItems: "center",
              padding: "8px 26px",
              backgroundColor: palette.catskillWhite,
              borderTopRightRadius: 24,
              borderBottomRightRadius: 24,
            }}
          >
            <BuildingIcon fill={palette.pickledBluewood} width={32} height={32} />

            <span
              css={{
                fontWeight: 700,
                letterSpacing: 0.8,
                fontSize: 14,
                lineHeight: `${14 * 1.333}px`,
                color: palette.pickledBluewood,
              }}
            >
              Imóveis
            </span>
          </div>

          <div
            css={{
              height: "100%",
            }}
          >
            <input
              spellCheck={false}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              placeholder="Digite para pesquisar os imóveis..."
              maxLength={18}
              value={searchQuery}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(event.currentTarget.value)}
              css={{
                boxSizing: "border-box",
                width: "100%",
                height: "100%",
                padding: "0px 20px",
                border: `solid 1px ${palette.mystic}`,
                borderRadius: 24,
                outline: "none",
                "&:focus": {
                  border: `solid 1px ${palette.royalBlue}`,
                },
                "&::placeholder": {
                  color: palette.heather,
                },
              }}
            />
          </div>

          <div
            css={{
              display: "grid",
              gridAutoColumns: "max-content",
              gridAutoFlow: "column",
              gridColumnGap: 16,
              paddingRight: 24,
              "& > div > strong": {
                color: palette.fiord,
                fontWeight: 700,
                fontSize: 14,
                lineHeight: `${14 * 1.333}px`,
              },
              "& > div > span": {
                color: palette.blueBayoux,
                fontWeight: 500,
                fontSize: 14,
                lineHeight: `${14 * 1.333}px`,
                letterSpacing: 0.5,
              },
            }}
          >
            <div
              css={{
                display: "grid",
                gridRowGap: 4,
              }}
            >
              <strong>{realEstateFund.overview.totalOfRealEstateProperties}</strong>
              <span>{realEstateFund.overview.totalOfRealEstateProperties === 1 ? "Imóvel" : "Imóveis"}</span>
            </div>

            <div
              css={{
                display: "grid",
                gridRowGap: 4,
              }}
            >
              <strong>{realEstateFund.overview.totalOfStates}</strong>
              <span>{realEstateFund.overview.totalOfStates === 1 ? "Estado" : "Estados"}</span>
            </div>

            <div
              css={{
                display: "grid",
                gridRowGap: 4,
              }}
            >
              <strong>{realEstateFund.overview.totalOfRegions}</strong>
              <span>{realEstateFund.overview.totalOfRegions === 1 ? "Região" : "Regiões"}</span>
            </div>

            <div
              css={{
                display: "grid",
                gridRowGap: 4,
              }}
            >
              <strong>
                {numeral(realEstateFund.overview.totalOfGrossLeaseableArea).format("0,000").replace(",", ".")}
              </strong>
              <span>ABL (m²)</span>
            </div>
          </div>
        </div>

        <table
          css={{
            width: "100%",
            overflow: "hidden",
            borderCollapse: "collapse",
            borderSpacing: 0,
          }}
        >
          <thead>
            <tr
              css={{
                backgroundColor: palette.catskillWhite,
                "& > th": {
                  padding: "12px 0px",
                  color: palette.pickledBluewood,
                  fontWeight: 700,
                  fontSize: 14,
                  lineHeight: `${14 * 1.333}px`,
                },
              }}
            >
              <th>Imóvel</th>

              <th>Endereço</th>

              <th
                css={{
                  cursor: "pointer",
                  userSelect: "none",
                }}
                onClick={() => {
                  if (sortDirection === "") {
                    setSortBy("location.city");
                    setSortDirection("asc");
                  } else if (sortDirection === "asc") {
                    setSortBy("location.city");
                    setSortDirection("desc");
                  } else if (sortDirection === "desc") {
                    setSortBy("");
                    setSortDirection("");
                  }
                }}
              >
                <div
                  css={{
                    display: "grid",
                    gridColumnGap: 4,
                    gridTemplateColumns: "max-content max-content",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                  }}
                >
                  <span>Cidade</span>
                  {sortDirection === "" && <SortIcon />}
                  {sortBy === "location.city" && sortDirection === "asc" && <SortAscendingIcon />}
                  {sortBy === "location.city" && sortDirection === "desc" && <SortDescendingIcon />}
                </div>
              </th>

              <th
                css={{
                  cursor: "pointer",
                  userSelect: "none",
                }}
                onClick={() => {
                  if (sortDirection === "") {
                    setSortBy("location.state");
                    setSortDirection("asc");
                  } else if (sortDirection === "asc") {
                    setSortBy("location.state");
                    setSortDirection("desc");
                  } else if (sortDirection === "desc") {
                    setSortBy("");
                    setSortDirection("");
                  }
                }}
              >
                <div
                  css={{
                    display: "grid",
                    gridColumnGap: 4,
                    gridTemplateColumns: "max-content max-content",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                  }}
                >
                  <span>Estado</span>
                  {sortDirection === "" && <SortIcon />}
                  {sortBy === "location.state" && sortDirection === "asc" && <SortAscendingIcon />}
                  {sortBy === "location.state" && sortDirection === "desc" && <SortDescendingIcon />}
                </div>
              </th>

              <th
                css={{
                  cursor: "pointer",
                  userSelect: "none",
                }}
                onClick={() => {
                  if (sortDirection === "") {
                    setSortBy("area");
                    setSortDirection("asc");
                  } else if (sortDirection === "asc") {
                    setSortBy("area");
                    setSortDirection("desc");
                  } else if (sortDirection === "desc") {
                    setSortBy("");
                    setSortDirection("");
                  }
                }}
              >
                <div
                  css={{
                    display: "grid",
                    gridColumnGap: 4,
                    gridTemplateColumns: "max-content max-content",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                  }}
                >
                  <span>Área (m²)</span>

                  {sortDirection === "" && <SortIcon />}
                  {sortBy === "area" && sortDirection === "asc" && <SortAscendingIcon />}
                  {sortBy === "area" && sortDirection === "desc" && <SortDescendingIcon />}
                </div>
              </th>

              {propertiesToRent.some((property) => property.vacancyRate !== null) && (
                <th
                  css={{
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                  style={{
                    padding: "0px 16px",
                  }}
                  onClick={() => {
                    if (sortDirection === "") {
                      setSortBy("vacancyRate");
                      setSortDirection("asc");
                    } else if (sortDirection === "asc") {
                      setSortBy("vacancyRate");
                      setSortDirection("desc");
                    } else if (sortDirection === "desc") {
                      setSortBy("");
                      setSortDirection("");
                    }
                  }}
                >
                  <div
                    css={{
                      display: "grid",
                      gridColumnGap: 4,
                      gridTemplateColumns: "max-content max-content",
                      alignItems: "center",
                      justifyContent: "center",
                      textAlign: "center",
                    }}
                  >
                    <span>Vacância</span>

                    {sortDirection === "" && <SortIcon />}
                    {sortBy === "vacancyRate" && sortDirection === "asc" && <SortAscendingIcon />}
                    {sortBy === "vacancyRate" && sortDirection === "desc" && <SortDescendingIcon />}
                  </div>
                </th>
              )}
            </tr>
          </thead>

          <tbody>
            {pagination.currentItems.length === 0 && searchQuery !== "" && (
              <tr>
                <td
                  colSpan={7}
                  css={{
                    textAlign: "center",
                    padding: 16,
                    fontSize: 14,
                    fontWeight: 500,
                    lineHeight: `${14 * 1.333}px`,
                    color: palette.pickledBluewood,
                    letterSpacing: 0.5,
                    maxWidth: 0,
                  }}
                >
                  Nenhum resultado encontrado.
                </td>
              </tr>
            )}

            {pagination.currentItems
              .map((property, index) => {
                if (!property.location) {
                  return (
                    <tr
                      // eslint-disable-next-line react/no-array-index-key
                      key={`${property.id}/${index}`}
                      css={{
                        "& > td": {
                          maxWidth: 0,
                          padding: "8px 16px",
                          color: palette.pickledBluewood,
                          fontWeight: 500,
                          fontSize: 14,
                          lineHeight: `${14 * 1.333}px`,
                          letterSpacing: 0.5,
                          textAlign: "center",
                        },
                      }}
                      style={{
                        backgroundColor: index % 2 ? palette.catskillWhite : "none",
                      }}
                    >
                      <td
                        css={{
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                          textOverflow: "ellipsis",
                        }}
                      >
                        <span title={property.name}>{property.name}</span>
                      </td>

                      <td
                        css={{
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {property.address}
                      </td>

                      <td
                        css={{
                          width: "15%",
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                          textOverflow: "ellipsis",
                        }}
                      >
                        -
                      </td>

                      <td
                        css={{
                          width: "8%",
                        }}
                      >
                        -
                      </td>

                      <td
                        css={{
                          width: "10%",
                        }}
                      >
                        {numeral(property.area).format("0,000").replace(",", ".")}
                      </td>

                      {property.vacancyRate !== null && (
                        <td
                          css={{
                            width: "12%",
                          }}
                        >
                          {(property.vacancyRate * 100).toFixed(0)}%
                        </td>
                      )}
                    </tr>
                  );
                }

                return (
                  <tr
                    // eslint-disable-next-line react/no-array-index-key
                    key={`${property.id}/${index}`}
                    css={{
                      "& > td": {
                        maxWidth: 0,
                        padding: "8px 16px",
                        color: palette.pickledBluewood,
                        fontWeight: 500,
                        fontSize: 14,
                        lineHeight: `${14 * 1.333}px`,
                        letterSpacing: 0.5,
                        textAlign: "center",
                      },
                    }}
                    style={{
                      backgroundColor: index % 2 ? palette.catskillWhite : "none",
                    }}
                  >
                    <td
                      css={{
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                      }}
                    >
                      <span title={property.name}>{property.name}</span>
                    </td>

                    <td
                      css={{
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                      }}
                    >
                      <span title={property.address}>{property.location.formatted}</span>
                    </td>

                    <td
                      css={{
                        width: "15%",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {property.location.city ? (
                        <span title={property.location.city}>{property.location.city}</span>
                      ) : (
                        "-"
                      )}
                    </td>

                    <td
                      css={{
                        width: "8%",
                      }}
                    >
                      {property.location.abbreviatedStateName || "-"}
                    </td>

                    <td
                      css={{
                        width: "10%",
                      }}
                    >
                      {numeral(property.area).format("0,000").replace(",", ".")}
                    </td>

                    {property.vacancyRate !== null && (
                      <td
                        css={{
                          width: "12%",
                        }}
                      >
                        {(property.vacancyRate * 100).toFixed(0)}%
                      </td>
                    )}
                  </tr>
                );
              })
              .filter(Boolean)}
          </tbody>
        </table>

        {pagination.pages.length > 1 && (
          <div
            css={{
              padding: "0px 24px",
              textAlign: "right",
            }}
          >
            <div
              css={{
                display: "inline-grid",
                gridAutoColumns: "max-content",
                gridAutoFlow: "column",
                gridColumnGap: 12,
              }}
            >
              {pagination.pages.map((page) => {
                return (
                  <button
                    type="button"
                    key={page}
                    onClick={() => pagination.setCurrentPage(page)}
                    css={{
                      display: "inline-flex",
                      justifyContent: "center",
                      alignItems: "center",
                      margin: 0,
                      boxShadow: pagination.currentPage === page ? `0 4px 10px 0 ${palette.tropicalBlue}` : undefined,
                      border: "none",
                      borderRadius: 12,
                      width: 32,
                      height: 32,
                      padding: 0,
                      backgroundColor: pagination.currentPage === page ? palette.royalBlue : palette.selago,
                      cursor: "pointer",
                      outline: "none",
                      fontSize: 14,
                      lineHeight: `${14 * 1.333}px`,
                      fontWeight: 700,
                      letterSpacing: 0.5,
                      color: pagination.currentPage === page ? palette.white : palette.royalBlue,
                      userSelect: "none",
                      "&:hover": {
                        boxShadow: `0 4px 10px 0 ${palette.tropicalBlue}`,
                      },
                    }}
                  >
                    {page}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <GalleryLightbox
        pictures={realEstateFund.gallery as Omit<IBrazilianRealEstateFundGalleryPicture, "__typename">[]}
      >
        {({ showPicture }) => (
          <GalleryCarousel realEstateFund={realEstateFund} onPictureClick={(picture) => showPicture(picture)} />
        )}
      </GalleryLightbox>
    </div>
  );
}
